/**
 * SSO (SC-2621, SC-2720) — shared constants for the backend-mediated sign-in
 * flow, shared across every provider (Google, Microsoft, …).
 *
 * The flow is backend-mediated: each "Continue with <provider>" button is a
 * full-page browser navigation (NOT fetch/XHR — the endpoint 307-redirects to
 * the provider's consent screen, which CORS can't follow), and the BE redirects
 * back to `/auth/callback` with the result in the URL fragment.
 */

import { API_BASE } from '../api/client';

// Built off the shared `API_BASE` (client.ts) so the origin + its local-dev
// fallback live in exactly one place. `provider` selects the BE connection
// (`google` | `microsoft`). NOTE: `API_BASE` already includes the `/api/v1`
// prefix in this repo (see envs/.env.staging.example), so append only the SSO
// path — do NOT re-add `/api/v1` (the BE handoff's sketch assumed a bare host).
// This must be a full-page nav, not fetch (the endpoint 307s to the provider).
export function ssoAuthorizeUrl(provider: 'google' | 'microsoft' = 'google'): string {
  return `${API_BASE}/auth/sso/${provider}/authorize`;
}

// SC-2780 — client nonce that closes the FE-fragment token-injection vector
// (vector 2; SC-2711's BE binding cookie closes the BE-mediated vector 1).
//
// The tab that starts the flow mints an opaque nonce, stashes it in
// `sessionStorage` (per-tab; survives the cross-origin round-trip to the
// provider and back), and passes it to `/authorize?client_nonce=…`. The BE
// carries it opaquely and echoes it back in the callback fragment; the callback
// then requires the fragment nonce to match this tab's stashed value BEFORE
// hydrating tokens. An attacker-injected `#access_token=…` fragment lands in a
// tab that never ran `beginSso`, so it has no matching nonce → rejected.
//
// The BE bounds `client_nonce` at 128 chars (oversized → dropped, un-echoed);
// `crypto.randomUUID()` is 36 chars (122 bits of entropy) — comfortably under.
const SSO_NONCE_KEY = 'sso_client_nonce';

// Mint + stash a fresh nonce and return the authorize URL carrying it. Side-
// effecting (writes sessionStorage), so call it from the click handler — not in
// render. THROWS if the nonce can't be minted or stashed — no Web Crypto
// (`crypto.randomUUID` needs a secure context, which HTTPS and localhost both
// satisfy) or hard-locked storage — so the caller aborts navigation and shows an
// error, rather than starting a provider flow with a nonce the callback can
// never match (which would strand the user on a generic "sign-in failed").
export function beginSso(provider: 'google' | 'microsoft' = 'google'): string {
  const nonce = crypto.randomUUID();
  sessionStorage.setItem(SSO_NONCE_KEY, nonce);
  return `${ssoAuthorizeUrl(provider)}?client_nonce=${encodeURIComponent(nonce)}`;
}

// Read the stashed nonce and remove it (single-use) — call once per callback,
// on every path, so a stale nonce can't linger into a later flow. Returns null
// when none was stashed (direct visit, or an injected fragment in a fresh tab).
export function readAndClearSsoNonce(): string | null {
  try {
    const nonce = sessionStorage.getItem(SSO_NONCE_KEY);
    sessionStorage.removeItem(SSO_NONCE_KEY);
    return nonce;
  } catch {
    return null;
  }
}

// Maps the BE's `#error=<code>` on the callback fragment to user-facing copy.
// The fragment carries no provider marker, so copy stays provider-neutral (a
// Microsoft user must never see "Google" wording, and vice versa).
export const SSO_ERROR_COPY: Record<string, string> = {
  not_provisioned: 'No EKOM account for this email — contact your administrator.',
  email_unverified: "Your account's email isn't verified.",
  provider_error: 'Sign-in was cancelled. Try again.',
};

export const SSO_ERROR_FALLBACK = 'Sign-in failed. Please try again.';

export function ssoErrorCopy(code: string | null | undefined): string {
  return (code && SSO_ERROR_COPY[code]) || SSO_ERROR_FALLBACK;
}
